import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js"; // Reused component
import Features from "../../../../Components/Features/ThreeColumnPages"; // Reused component
import {
  FaUserTie,
  FaPaintBrush,
  FaUsers,
  FaHandshake,
  FaAward,
  FaMapMarkerAlt,
  FaChartLine,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

// Professional UI/UX Designers in Dubai Page Component
export const ProfessionalUIUXDesignersInDubai = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Professional UI/UX Designers in Dubai" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Work with the Best in the Industry</Subheading>}
        heading={
          <Heading>
            Elevate Your Brand with{" "}
            <span tw="text-primary-500">
              Professional UI/UX Designers in Dubai
            </span>
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we bring together the most talented and
            experienced UI/UX designers in Dubai. Our team combines creativity,
            strategy, and innovation to help you engage your audience, grow your
            brand, and achieve measurable results through exceptional digital
            experiences.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80", // Designer working on UI/UX project
          "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80", // Professional designer at work
          "https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=1169&q=80", // Design team collaborating
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Get Started Today"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Our Designers?</Subheading>}
        heading="Your Trusted Partner for Professional UI/UX Designers in Dubai"
        description="We combine expertise, creativity, and innovation to deliver unmatched value."
        cards={[
          {
            icon: <FaUserTie color="#ff5464" size={40} />,
            title: "Experienced Designers",
            description:
              "Our team consists of seasoned professionals with years of industry experience.",
          },
          {
            icon: <FaPaintBrush color="#ff5464" size={40} />,
            title: "Creative Excellence",
            description:
              "Craft visually stunning and intuitive designs that captivate your audience.",
          },
          {
            icon: <FaMapMarkerAlt color="#ff5464" size={40} />,
            title: "Localized Expertise",
            description:
              "Understand Dubai's unique market trends and diverse demographics.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "User-Centric Focus",
            description:
              "Prioritize usability, accessibility, and engagement for all users.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client-Centric Collaboration",
            description:
              "Work closely with you to ensure alignment with your vision and goals.",
          },
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Proven Track Record",
            description:
              "Delivering exceptional results for Dubai clients since [Year].",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Streamlined Process for Delivering Exceptional Designs in Dubai"
        description={
          <Description>
            Our Dubai-focused process ensures quality, efficiency, and
            measurable results:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Discovery & Research: Understand your business, audience, and
                goals.
              </li>
              <li>
                User Analysis: Identify your target demographic and their
                preferences.
              </li>
              <li>
                Wireframing & Prototyping: Create interactive prototypes for
                testing and feedback.
              </li>
              <li>
                Design Execution: Craft visually stunning and intuitive designs
                tailored for your audience.
              </li>
              <li>
                Development Integration: Collaborate with developers to bring
                your designs to life.
              </li>
              <li>
                Optimization & Feedback: Continuously refine designs based on
                user feedback and performance metrics.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80", // Designer sketching UI concepts
          "https://images.unsplash.com/photo-1558655146-9f40138edfeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=1164&q=80", // Design sprint process
          "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80", // User interface design work
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Results That Speak for Themselves"
        description="Here’s how our professional UI/UX designers have empowered businesses in Dubai."
        cards={[
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Improved User Engagement",
            description:
              "Helped an e-commerce platform in Dubai increase user retention by 150% through intuitive design.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description:
              "98% client retention rate through consistent excellence and measurable results.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Performance Boost",
            description:
              "Achieved a 200% increase in conversions for a local retail app using optimized UX strategies.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={
          <Subheading>Ready to Work with Top-Tier Designers?</Subheading>
        }
        heading="Let’s Create Impactful Solutions Together in Dubai"
        description={
          <Description>
            Partner with Dubai’s leading professional UI/UX designers to unlock
            your business’s full potential. Whether you’re launching a website,
            mobile app, or software, we’re here to help.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80", // Team of professional designers
          "https://images.unsplash.com/photo-1570057633712-a553694c35ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=1631&q=80", // Dubai business district skyline
          "https://images.unsplash.com/photo-1542744094-3a31f272c490?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80", // Business professionals handshake
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Today"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
